import React, { ReactElement, useMemo, useRef } from 'react';
import { useSuspenseInfiniteQuery } from '@tanstack/react-query';
import { Table, TableProps } from 'antd';

import { SorterResult, SortOrder } from 'antd/es/table/interface';
import { useTranslation } from 'react-i18next';

import { isHasAccess } from 'features/validateAccess';
import { CountryDTO, CountryService, useCountry, DEFAULT_PAGE_SIZE, DEFAULT_SORT_ORDER } from 'entities/country';
import { withSuspense, useElementHeight } from 'shared/lib/react';

import { Loader } from 'shared/ui/Loader';
import { COLSPAN_HIDDEN, columns, HEADER_HEIGHT, BORDER_HEIGHT } from '../../CountryTable.const';
import { UPDATE_COUNTRY } from '../../CountryTable.permissions';
import { CountryList } from '../../CountryTable.types';
import { CountryStatusSwitcher } from '../CountryStatusSwitcher';
import { EmptyTable } from '../EmptyTable';
import { ExpandIcon } from '../ExpandIcon';

import styles from './CountryTable.module.scss';

const CountryTable: React.FC = (): ReactElement => {
  const parentRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const { height: tableHeight } = useElementHeight(parentRef, BORDER_HEIGHT);

  const { sort, setSort, searchName } = useCountry();

  const { data: countriesData, isFetching } = useSuspenseInfiniteQuery(
    CountryService.queryOptions({ size: DEFAULT_PAGE_SIZE, sort, searchName }),
  );

  const handleSortingChange = (order?: SortOrder): void => {
    if (!order) {
      setSort(DEFAULT_SORT_ORDER);
      return;
    }
    setSort(order === 'ascend' ? ['name,asc'] : ['name,desc']);
  };

  const hideStatusColumn = !isHasAccess(UPDATE_COUNTRY);

  const countriesList = countriesData?.pages.reduce<CountryDTO[]>((acc, page) => [...acc, ...page.content], []);

  const tableData = countriesList.map(({ name, flagUrl, ...country }) => {
    return {
      ...country,
      nameWithFlag: { name, flagUrl },
    };
  });

  const onChange: TableProps<CountryList>['onChange'] = (_, __, sorter, extra) => {
    if (extra.action === 'sort') {
      const currentSorter = sorter as SorterResult;
      handleSortingChange(currentSorter.order);
    }
  };

  const activeCountryList = tableData.filter(({ active }) => active);
  const inactiveCountryList = tableData.filter(({ active }) => !active);

  const groupedData = useMemo(
    () => [
      {
        id: 'active-group',
        title: t('Country:CountryTable.columns.active.title', {
          count: activeCountryList.length,
        }),
        children: activeCountryList,
      },
      {
        id: 'inactive-group',
        title: t('Country:CountryTable.columns.inActive.title', {
          count: inactiveCountryList.length,
        }),
        children: inactiveCountryList,
      },
    ],
    [activeCountryList, inactiveCountryList, t],
  );

  const renderEmptyTable = (): ReactElement => {
    return (
      <div className={styles.emptyTable} style={{ height: tableHeight - HEADER_HEIGHT }}>
        <EmptyTable />
      </div>
    );
  };

  const countriesIsEmpty = !!countriesList.length;

  return (
    <div className={styles.container} ref={parentRef}>
      <Table
        rowKey={(row) => row.id}
        pagination={false}
        bordered
        loading={isFetching}
        onChange={onChange}
        className={styles.wrapperTable}
        columns={columns}
        locale={{
          emptyText: renderEmptyTable(),
        }}
        rowSelection={
          hideStatusColumn
            ? undefined
            : {
                hideSelectAll: true,
                onCell: (record) => {
                  return 'children' in record ? { colSpan: COLSPAN_HIDDEN } : {};
                },
                renderCell: (_, record) => {
                  return 'children' in record ? null : <CountryStatusSwitcher record={record} />;
                },
              }
        }
        sticky
        style={{ height: '100%' }}
        scroll={{ y: countriesIsEmpty ? tableHeight - HEADER_HEIGHT : undefined }}
        expandable={{
          indentSize: 0,
          expandRowByClick: true,
          expandIcon: ExpandIcon,
          defaultExpandedRowKeys: ['active-group'],
        }}
        dataSource={countriesIsEmpty ? groupedData : []}
      />
    </div>
  );
};

export default withSuspense(CountryTable, { fallback: <Loader /> });
